import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Administradores = () => {
  const [administradores, setAdministradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    nombre_completo: '',
    password: '',
    es_activo: true,
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchAdministradores();
  }, []);

  const fetchAdministradores = async () => {
    setLoading(true);
    try {
      const response = await api.get('/administradores');
      setAdministradores(response.data);
    } catch (error) {
      console.error('Error fetching administradores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (admin = null) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        username: admin.username,
        email: admin.email,
        nombre_completo: admin.nombre_completo || '',
        password: '',
        es_activo: admin.es_activo,
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        username: '',
        email: '',
        nombre_completo: '',
        password: '',
        es_activo: true,
      });
    }
    setFormError('');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingAdmin(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const dataToSend = { ...formData };
      if (!dataToSend.password) delete dataToSend.password;

      if (editingAdmin) {
        await api.put(`/administradores/${editingAdmin.id}`, dataToSend);
      } else {
        await api.post('/administradores', dataToSend);
      }
      fetchAdministradores();
      handleCloseModal();
    } catch (error) {
      setFormError(
        error.response?.data?.detail || 'Error al guardar el administrador'
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await api.put(`/administradores/${admin.id}`, {
        ...admin,
        es_activo: !admin.es_activo,
      });
      fetchAdministradores();
    } catch (error) {
      console.error('Error toggling admin status:', error);
      alert('Error al cambiar el estado del administrador');
    }
  };

  const handleDelete = async (admin) => {
    if (
      !window.confirm(
        `¿Estás seguro de eliminar al administrador "${admin.username}"?`
      )
    )
      return;

    try {
      await api.delete(`/administradores/${admin.id}`);
      fetchAdministradores();
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert(
        error.response?.data?.detail || 'Error al eliminar el administrador'
      );
    }
  };

  return (
    <div className="animate-fadeInUp">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Administradores</h2>
        <button
          onClick={() => handleOpenModal()}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] px-4 py-2 flex items-center space-x-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Nuevo Administrador</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {administradores.map((admin) => (
            <div
              key={admin.id}
              className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 border border-white/20 hover:scale-[1.02]"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white text-lg font-semibold">
                      {admin.nombre_completo?.charAt(0) ||
                        admin.username.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">
                      {admin.nombre_completo || admin.username}
                    </h3>
                    <p className="text-sm text-blue-200">@{admin.username}</p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleOpenModal(admin)}
                    className="p-1 text-blue-400 hover:text-blue-300 hover:bg-white/10 rounded transition-all"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>
                  {admin.id !== user?.id && (
                    <button
                      onClick={() => handleDelete(admin)}
                      className="p-1 text-red-400 hover:text-red-300 hover:bg-white/10 rounded transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <p className="text-sm text-blue-200 flex items-center space-x-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span>{admin.email}</span>
                </p>
                {admin.ultimo_acceso && (
                  <p className="text-xs text-blue-300/60">
                    Último acceso:{' '}
                    {new Date(admin.ultimo_acceso).toLocaleString()}
                  </p>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-white/20 flex justify-between items-center">
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    admin.es_activo
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                      : 'bg-red-500/20 text-red-300 border border-red-500/30'
                  }`}
                >
                  {admin.es_activo ? 'Activo' : 'Inactivo'}
                </span>
                <button
                  onClick={() => handleToggleActive(admin)}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  {admin.es_activo ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-white/20 animate-fadeInUp">
            <div className="border-b border-white/20 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                {editingAdmin ? 'Editar Administrador' : 'Nuevo Administrador'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-white/60 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {formError && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl">
                  <p className="text-red-300 text-sm">{formError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">
                    Usuario *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    name="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">
                    {editingAdmin
                      ? 'Nueva Contraseña (dejar vacío para mantener)'
                      : 'Contraseña *'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingAdmin}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="es_activo"
                      checked={formData.es_activo}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-500 rounded border-white/30 bg-white/10 focus:ring-blue-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-blue-200">
                      Usuario activo
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-white/20">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 border border-white/20"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50"
                >
                  {formLoading
                    ? 'Guardando...'
                    : editingAdmin
                      ? 'Actualizar'
                      : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Administradores;
